import { api, formatError } from '../api-client'
import { flushAndExit } from '../analytics'
import { parseArgs } from './context/args'
import { emitJSON } from './context/render'
import { resolveWorkspaceSlug } from './context'

// Integration CLI — SETUP: connect / confirm / disconnect external providers.
//
// F7.1 — the setup/execution split made real. Integration SETUP (connecting and
// configuring a provider) is a SEPARATE concern from broker EXECUTION (operating
// a connection that already exists). This command owns SETUP:
//   • catalog  — the connectable providers (from Nango)
//   • connect  — start a connect session (returns the embedded Connect UI session)
//   • confirm  — persist the authorized connection (idempotent per workspace+provider)
//   • list     — the workspace's integrations
//   • rm       — disconnect / delete an integration
// Once connected, you OPERATE it with 'driftless broker' (connections, operations,
// invoke, records, events, criterion). Authoring/deploying Nango actions is a
// third, human-only lane neither command touches (no-agent-scripting rule, F7.0).

// Help lives in ./help-text (light, dep-free) so index.ts can print it without
// importing this command body; re-exported here so INTEGRATION_HELP resolves from './integration'.
export { INTEGRATION_HELP } from './help-text'
import { INTEGRATION_HELP } from './help-text'

function printHelp(stream: NodeJS.WritableStream = process.stdout): void {
  stream.write(INTEGRATION_HELP)
}

export function formatIntegrationRow(i: Record<string, unknown>): string {
  // Integrations carry { id, type, ... }; a Nango connection's type is
  // 'nango:<provider>'. Render id + type without ever printing "undefined".
  const id = String(i['id'] ?? '—')
  const type = String(i['type'] ?? '—')
  return `${id.padEnd(28)} ${type}`
}

export async function integrationCommand(args: string[]): Promise<void> {
  const { flags, positional } = parseArgs(args)
  const sub = positional[0]
  const isJSON = !!flags['json']

  if (flags['help'] === true || sub === 'help') { printHelp(); process.exit(0) }
  if (sub === undefined) { printHelp(process.stderr); process.exit(1) }

  const ws = await resolveWorkspaceSlug()

  try {
    switch (sub) {
      case 'list': {
        const rows = (await api.get(`/workspaces/${ws}/integrations`)) as Array<Record<string, unknown>>
        if (isJSON) { emitJSON(rows); break }
        if (!rows.length) { console.log("No integrations yet. Connect one: driftless integration catalog, then 'integration connect <provider>'."); break }
        for (const i of rows) console.log(formatIntegrationRow(i))
        break
      }
      case 'catalog': {
        const rows = (await api.get(`/workspaces/${ws}/integrations/nango/catalog`)) as unknown
        if (isJSON) { emitJSON(rows); break }
        const list = Array.isArray(rows) ? rows : []
        if (!list.length) { console.log('No connectable providers configured in Nango.'); break }
        for (const p of list) {
          const o = p as Record<string, unknown>
          const provider = String(o['unique_key'] ?? o['provider'] ?? o['providerConfigKey'] ?? o['name'] ?? '—')
          console.log(provider)
        }
        console.log("\nConnect one:  driftless integration connect <provider>")
        break
      }
      case 'connect': {
        const provider = positional[1]
        if (!provider) { console.error("Error: 'integration connect' needs a <provider>."); process.exit(1) }
        const session = (await api.post(`/workspaces/${ws}/integrations/nango/${encodeURIComponent(provider)}/connect-session`, {})) as Record<string, unknown>
        if (isJSON) { emitJSON(session); break }
        console.log(`Connect session started for ${provider}.`)
        console.log('Authorize in the Connect UI, then run:')
        console.log(`  driftless integration confirm ${provider}`)
        console.log(`\nSession: ${JSON.stringify(session, null, 2)}`)
        break
      }
      case 'confirm': {
        const provider = positional[1]
        if (!provider) { console.error("Error: 'integration confirm' needs a <provider>."); process.exit(1) }
        const result = (await api.post(`/workspaces/${ws}/integrations/nango/${encodeURIComponent(provider)}/confirm`, {})) as Record<string, unknown>
        if (isJSON) { emitJSON(result); break }
        console.log(`✓ ${provider} connected.`)
        console.log(`  Operate it:  driftless broker operations ${provider}`)
        break
      }
      case 'rm':
      case 'delete':
      case 'disconnect': {
        const id = positional[1]
        if (!id) { console.error("Error: 'integration rm' needs an <id>."); process.exit(1) }
        const result = (await api.delete(`/workspaces/${ws}/integrations/${encodeURIComponent(id)}`)) as Record<string, unknown>
        if (isJSON) { emitJSON(result); break }
        console.log(`✓ integration ${id} disconnected.`)
        break
      }
      default:
        console.error(`Unknown integration subcommand: '${sub}'. Run 'driftless integration --help'.`)
        process.exit(1)
    }
    await flushAndExit(0)
  } catch (e: unknown) {
    console.error(formatError(e as Error))
    await flushAndExit(1)
  }
}
